import {
  CONSENTABLE_NOTIFICATION_CHANNELS,
  campaignPeriodStatusSchema,
  campaignPricingModeSchema,
  currencySchema,
  FORM_FILE_MAX_BYTES,
} from '@btsis/core';
import { oc } from '@orpc/contract';
import { z } from 'zod';
import { commonErrors, formErrors } from './common/errors.js';
import { formFileSchema, formFileUploadInputSchema } from './common/form-file.js';
import { formSchemaDocumentSchema } from './common/form-schema.js';
import { minorAmountSchema, moneySchema } from './common/money.js';
import { organizationBrandingContract } from './common/organization-branding.js';
import { paginated, paginationInputSchema } from './common/pagination.js';
import { phoneSchema } from './common/phone.js';
import { campaignQuoteContract } from './common/quote.js';
import { fileNameSchema, presignedUploadSchema, uploadTokenSchema } from './common/upload.js';

export type { FormSchemaDocument } from './common/form-schema.js';
export type { MoneyDto } from './common/money.js';
export type { CampaignQuote } from './common/quote.js';

const portalErrors = {
  NOT_FOUND: commonErrors.NOT_FOUND,
  TOO_MANY_REQUESTS: commonErrors.TOO_MANY_REQUESTS,
  VALIDATION_FAILED: commonErrors.VALIDATION_FAILED,
};

/**
 * What the donor's page says about the season, decided on the server against
 * its own clock. Status and the calendar window are separate facts and a
 * browser clock is not evidence of either — see
 * docs/decisions/0016-campaign-period-lifecycle.md.
 */
export const portalPeriodStateSchema = z.enum(['open', 'upcoming', 'ended', 'paused']);

export type PortalPeriodState = z.infer<typeof portalPeriodStateSchema>;

export const portalCategorySchema = z.object({
  id: z.uuid(),
  name: z.string(),
  slug: z.string(),
  icon: z.string().nullable(),
});

export type PortalCategory = z.infer<typeof portalCategorySchema>;

/**
 * Progress as the donor may see it: the goal, and the two measures a bar draws.
 * `fulfillment_cost_minor` and the donation count are operational figures and
 * are deliberately absent from a public payload.
 *
 * `collected` and `pledged` are in the goal's own currency: every other
 * currency is converted at the latest rate day on which each rate it needs
 * exists, which `convertedAt` names (`YYYY-MM-DD`), and a currency with no rate
 * at all is named in `unconvertedCurrencies` rather than converted at an
 * invented one — the same answer `campaign.period.totals` gives. See 0014 and
 * 0019.
 */
export const portalProgressSchema = z.object({
  goal: moneySchema,
  collected: moneySchema,
  pledged: moneySchema,
  unconvertedCurrencies: z.array(currencySchema),
  convertedAt: z.string().nullable(),
});

export type PortalProgress = z.infer<typeof portalProgressSchema>;

export const portalPeriodSchema = z.object({
  id: z.uuid(),
  seasonYear: z.number().int().nullable(),
  startsAt: z.string(),
  endsAt: z.string(),
  status: campaignPeriodStatusSchema,
  state: portalPeriodStateSchema,
  /** Whether `campaign.quote` will price this period right now. */
  isOpen: z.boolean(),
});

export type PortalPeriod = z.infer<typeof portalPeriodSchema>;

/**
 * One period's prices in one currency. Which columns carry a value is decided
 * by the campaign's `pricingMode` at write time, so a screen reads the mode and
 * the row rather than guessing from which fields happen to be null.
 */
export const portalPriceSchema = z.object({
  currency: currencySchema,
  unitAmount: moneySchema.nullable(),
  minAmount: moneySchema.nullable(),
  maxAmount: moneySchema.nullable(),
  suggestedAmounts: z.array(moneySchema),
  isDefault: z.boolean(),
});

export type PortalPrice = z.infer<typeof portalPriceSchema>;

export const portalCampaignSummarySchema = z.object({
  id: z.uuid(),
  name: z.string(),
  slug: z.string(),
  category: portalCategorySchema,
  pricingMode: campaignPricingModeSchema,
  unitLabel: z.string().nullable(),
  unitsPerGroup: z.number().int().nullable(),
  /** The campaign's own prose, Turkish content typed by an administrator. */
  description: z.string().nullable(),
  /**
   * A short-lived signed `GET` for the campaign's picture, minted for anyone
   * the publication decision already lets read this campaign — that decision,
   * not a seat, is what authorizes it (0036). It is `null` where no picture has
   * been set, where its scan has not passed, or where object storage is not
   * configured, and the card falls back to the category's icon. The object key
   * is never sent.
   */
  imageUrl: z.string().nullable(),
  period: portalPeriodSchema,
  /** The price row the campaign leads with, so a card can show a figure. */
  defaultPrice: portalPriceSchema.nullable(),
  progress: portalProgressSchema.nullable(),
});

export type PortalCampaignSummary = z.infer<typeof portalCampaignSummarySchema>;

/**
 * `formSchema` is the campaign's **published** document, already filtered to
 * the `portal` audience on the server: a field with `portalVisible: false` or
 * `adminOnly: true` is not in the payload at all, so it cannot be rendered,
 * required or submitted by a donor — see
 * docs/decisions/0012-versioned-form-schema-compiled-to-zod.md.
 */
export const portalCampaignSchema = portalCampaignSummarySchema.extend({
  formVersion: z.number().int().min(0),
  /**
   * Whether this campaign takes a standing gift — `campaigns.allows_recurring`,
   * configuration applied uniformly rather than anything the screen decides
   * about a campaign's identity (`.claude/rules/campaign-engine.md`).
   */
  allowsRecurring: z.boolean(),
  formSchema: formSchemaDocumentSchema,
  /**
   * The answers of `formSchema` the price depends on — a sheep and a cattle
   * share, a normal and an urgent slaughter. The page sends these, and only
   * these, to `campaign.quote` as the donor answers them, so the figure it shows
   * is the one the basket prices and the order charges. Filtered to the portal
   * audience with the form, so an admin-only price answer is not named here
   * (docs/decisions/0072-a-price-may-depend-on-the-answers-and-a-group-on-the-animal.md).
   */
  priceFieldKeys: z.array(z.string()),
  /**
   * The season's own price per currency, unconditional. A figure that depends
   * on the answers comes from `campaign.quote`, never from here.
   */
  prices: z.array(portalPriceSchema),
});

export type PortalCampaign = z.infer<typeof portalCampaignSchema>;

/**
 * The two routes the public donation site reads, both unauthenticated and both
 * throttled per address. They carry no scope predicate because there is no
 * seat: what a donor may see is `portal_visibility` plus the period lifecycle,
 * decided in SQL in `PortalCampaignRepository` and nowhere else.
 */
export const portalCampaignContract = {
  /**
   * Only periods that are `active`, inside `[starts_at, ends_at)` and
   * `portal_visibility = 'public'`. `unlisted` is reachable by slug and absent
   * here; `internal` exists for neither. `categories` lists every category the
   * donor could browse regardless of the current filter, so choosing one does
   * not empty the picker that chose it.
   */
  list: oc
    .route({
      method: 'GET',
      path: '/portal/campaigns',
      summary: 'Public campaigns open for donation right now',
    })
    .errors(portalErrors)
    .input(
      paginationInputSchema.extend({
        pageSize: z.coerce.number().int().min(1).max(60).default(24),
        categorySlug: z
          .string()
          .trim()
          .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/)
          .optional(),
      }),
    )
    .output(
      paginated(portalCampaignSummarySchema).extend({
        categories: z.array(portalCategorySchema),
      }),
    ),

  /**
   * `public` and `unlisted` alike, and a period that is not open right now
   * still renders — a donor holding a real link is told "not yet" or "over",
   * which is the answer 0032 gives the quote one tier down. An `internal`
   * period, a `draft`, a `closed` one without `show_when_closed` and an
   * archived campaign are all **404**: the existence of the campaign is itself
   * not public — see docs/decisions/0016-campaign-period-lifecycle.md.
   */
  getBySlug: oc
    .route({
      method: 'GET',
      path: '/portal/campaigns/{slug}',
      summary: 'One public campaign with its period, prices and donation form',
    })
    .errors(portalErrors)
    .input(
      z.object({
        slug: z
          .string()
          .trim()
          .min(2)
          .max(160)
          .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/),
      }),
    )
    .output(z.object({ campaign: portalCampaignSchema })),
};

/**
 * One campaign's line in the donor's basket. The browser sends what was chosen
 * — a quantity, or an amount in integer minor units — and never a total: every
 * figure comes back from `buildQuote` on the server, as it does for the quote
 * one tier down (docs/decisions/0013).
 */
export const portalBasketLineSchema = z.object({
  campaignPeriodId: z.uuid(),
  quantity: z.coerce.number().int().min(1).max(999).optional(),
  amountMinor: minorAmountSchema.optional(),
  formData: z.record(z.string(), z.unknown()).default({}),
});

export type PortalBasketLine = z.infer<typeof portalBasketLineSchema>;

/**
 * The basket carries **one currency**. It is paid with one link, and a link is
 * drawn in one currency on one organization's account (docs/decisions/0053), so
 * a campaign with no price in the basket's currency cannot be in it.
 */
export const portalBasketSchema = z.object({
  currency: currencySchema,
  lines: z.array(portalBasketLineSchema).min(1).max(20),
});

export type PortalBasket = z.infer<typeof portalBasketSchema>;

export const portalPricedLineSchema = z.object({
  campaignPeriodId: z.uuid(),
  campaignId: z.uuid(),
  name: z.string(),
  slug: z.string(),
  unitLabel: z.string().nullable(),
  quantity: z.number().int().nullable(),
  unitAmount: moneySchema.nullable(),
  amount: moneySchema,
});

export type PortalPricedLine = z.infer<typeof portalPricedLineSchema>;

export const portalBasketQuoteSchema = z.object({
  currency: currencySchema,
  lines: z.array(portalPricedLineSchema),
  total: moneySchema,
});

export type PortalBasketQuote = z.infer<typeof portalBasketQuoteSchema>;

export const portalDonorKindSchema = z.enum(['new', 'returning', 'anonymous']);

export type PortalDonorKind = z.infer<typeof portalDonorKindSchema>;

/**
 * How the donor said who they are. `returning` carries an address and nothing
 * else: the server may recognise it, and **the answer is never in the
 * response** — reading a donor's own history needs the credential §6.3
 * attaches, and a public route that confirmed an address was known would be an
 * account oracle.
 */
export const portalOrderDonorSchema = z
  .object({
    kind: portalDonorKindSchema,
    name: z.string().trim().min(2).max(160).optional(),
    email: z.email().max(254).optional(),
    phone: phoneSchema.optional(),
    countryIso: z
      .string()
      .trim()
      .length(2)
      .regex(/^[A-Za-z]{2}$/)
      .optional(),
    communicationConsent: z.boolean().default(false),
  })
  .superRefine((donor, context) => {
    if (donor.kind === 'anonymous') return;
    if (!donor.email) {
      context.addIssue({ code: 'custom', path: ['email'], message: 'PORTAL_DONOR_EMAIL_REQUIRED' });
    }
    if (donor.kind === 'new' && !donor.name) {
      context.addIssue({ code: 'custom', path: ['name'], message: 'PORTAL_DONOR_NAME_REQUIRED' });
    }
  });

export type PortalOrderDonor = z.infer<typeof portalOrderDonorSchema>;

/**
 * **The seam Phase 6 §6.2 fills in.** `not_configured` is what every order gets
 * today: the basket is priced, validated and stored, and no money has been
 * asked for. §6.2 draws a hosted-checkout link on an active payment account of
 * the collecting organization and answers `redirect` with its `url` —
 * docs/decisions/0053. Nothing else about the response changes.
 */
export const portalPaymentHandoffSchema = z.object({
  status: z.enum(['not_configured', 'redirect']),
  url: z.string().nullable(),
});

export type PortalPaymentHandoff = z.infer<typeof portalPaymentHandoffSchema>;

export const portalOrderSchema = z.object({
  id: z.uuid(),
  /**
   * `requested` is a no-charge request: its donations were created the
   * moment it was placed, with no payment step at all — never `paid`, which
   * says money moved (docs/decisions/0073-a-campaign-may-take-no-money-and-its-yurt-is-told.md).
   */
  status: z.enum(['awaiting_payment', 'paid', 'cancelled', 'requested']),
  total: moneySchema,
  lines: z.array(portalPricedLineSchema),
  createdAt: z.string(),
});

export type PortalOrder = z.infer<typeof portalOrderSchema>;

/**
 * What the donor's return page may say about a payment. **It is read from what
 * the callback has already recorded and from nothing else**: a donor can load
 * or forge a return URL, so the page confirms nothing on its own
 * (docs/decisions/0042).
 *
 * `expired` is the link whose time ran out, which is the expired-link page;
 * `redirect` hands back the live URL, so a donor who cancelled at the provider
 * can pay the same order again rather than building a second basket.
 */
export const portalOrderPaymentSchema = z.object({
  status: z.enum(['none', 'redirect', 'expired', 'paid']),
  url: z.string().nullable(),
  expiresAt: z.string().nullable(),
});

export type PortalOrderPayment = z.infer<typeof portalOrderPaymentSchema>;

/**
 * `references` are the reference codes of the donations the order became —
 * empty until a verified capture created them, which is the whole of what the
 * page is allowed to assert.
 */
export const portalOrderStateSchema = z.object({
  order: portalOrderSchema,
  payment: portalOrderPaymentSchema,
  references: z.array(z.string()),
});

export type PortalOrderState = z.infer<typeof portalOrderStateSchema>;

const orderErrors = {
  ...portalErrors,
  CONFLICT: commonErrors.CONFLICT,
  PRICING_VIOLATION: commonErrors.PRICING_VIOLATION,
  ...formErrors,
};

/**
 * One campaign's line in a **request**, never a basket: a `no_charge`
 * campaign has no amount, no currency and no quantity to send — 0073's own
 * rule, asked of the campaign's mode and never its name — so this carries
 * only what a request has: the season and the donor's answers. The bounds
 * mirror `portalBasketSchema`'s.
 */
export const portalRequestLineSchema = z.object({
  campaignPeriodId: z.uuid(),
  formData: z.record(z.string(), z.unknown()).default({}),
});

export type PortalRequestLine = z.infer<typeof portalRequestLineSchema>;

export const portalRequestSchema = z.object({
  lines: z.array(portalRequestLineSchema).min(1).max(20),
});

export type PortalRequest = z.infer<typeof portalRequestSchema>;

export const portalRequestResultSchema = z.object({
  orderId: z.uuid(),
  references: z.array(z.string()),
});

export type PortalRequestResult = z.infer<typeof portalRequestResultSchema>;

const requestErrors = {
  ...portalErrors,
  CONFLICT: commonErrors.CONFLICT,
  ...formErrors,
};

/**
 * The public site's one write. It stores what the donor chose and hands the
 * payment step to the seam above; it touches no donation, no total the
 * organization reports and no donor's history.
 */
export const portalOrderContract = {
  /**
   * Prices a whole basket without storing anything, so the basket page can show
   * a total it did not compute itself — .claude/rules/money.md. `POST` because
   * a basket does not fit a query string; it writes nothing.
   */
  quote: oc
    .route({ method: 'POST', path: '/portal/baskets/quote' })
    .errors(orderErrors)
    .input(portalBasketSchema)
    .output(portalBasketQuoteSchema),

  create: oc
    .route({ method: 'POST', path: '/portal/orders' })
    .errors(orderErrors)
    .input(z.object({ basket: portalBasketSchema, donor: portalOrderDonorSchema }))
    .output(z.object({ order: portalOrderSchema, payment: portalPaymentHandoffSchema })),

  /**
   * The return, cancel and expired-link page's one read. The order's id is the
   * capability — an unguessable UUIDv7 the donor was handed — and the answer is
   * narrowed to the site the host resolved, so one organization's site cannot
   * read another's order. It carries **nothing the donor typed**: no name, no
   * address, no answers. Public for the same reason the basket is, and on the
   * same throttle key.
   */
  get: oc
    .route({ method: 'GET', path: '/portal/orders/{id}' })
    .errors(orderErrors)
    .input(z.object({ id: z.uuid() }))
    .output(portalOrderStateSchema),

  /**
   * A no-charge campaign's sibling of `create`: the same validation, the same
   * throttle key and the same host-to-organization resolution, and none of
   * `create`'s pricing, currency or payment step — a request costs nothing,
   * so there is nothing to quote and nowhere to draw a link
   * (docs/decisions/0073-a-campaign-may-take-no-money-and-its-yurt-is-told.md,
   * docs/plans/10-operational-redesign.md §10.9). Every line's campaign must
   * be `no_charge`, or the write refuses `PORTAL_CAMPAIGN_CHARGES_MONEY`
   * rather than silently pricing it.
   */
  request: oc
    .route({ method: 'POST', path: '/portal/requests' })
    .errors(requestErrors)
    .input(z.object({ request: portalRequestSchema, donor: portalOrderDonorSchema }))
    .output(portalRequestResultSchema),
};

/**
 * **A donor's own state, decided on the server**, the same discipline
 * `portalPeriodState` already applies to a season: the two status axes a
 * donation carries are the organization's operational and financial views of
 * it (docs/decisions/0006), and neither is what a donor asked. `queued` and
 * `partially_settled` say nothing to the person who gave the money and say
 * something about the branch's workflow to anyone else reading over their
 * shoulder, so the account page is told one word and the client derives none
 * of it.
 */
export const portalAccountDonationStateSchema = z.enum([
  'requested',
  'awaiting_payment',
  'received',
  'fulfilled',
  'refunded',
  'cancelled',
]);

export type PortalAccountDonationState = z.infer<typeof portalAccountDonationStateSchema>;

/**
 * The donor's own record, as the donor may see it. It carries what the donor
 * gave us and nothing the organization decided about them: no institution, no
 * scope, no merge state, no `donor_id`. The organization is the site's, named
 * so the page can say whose records these are.
 */
export const portalAccountDonorSchema = z.object({
  name: z.string().nullable(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  address: z.string().nullable(),
  countryIso: z.string().nullable(),
  communicationConsent: z.boolean(),
  consentRecordedAt: z.string().nullable(),
  organizationName: z.string(),
});

export type PortalAccountDonor = z.infer<typeof portalAccountDonorSchema>;

export const portalPrivacyRequestKindSchema = z.enum(['export', 'erasure']);

export type PortalPrivacyRequestKind = z.infer<typeof portalPrivacyRequestKindSchema>;

export const portalPrivacyRequestSchema = z.object({
  id: z.uuid(),
  kind: portalPrivacyRequestKindSchema,
  requestedAt: z.string(),
});

export type PortalPrivacyRequest = z.infer<typeof portalPrivacyRequestSchema>;

/**
 * `openRequests` is bounded by `donor_privacy_requests_one_open`: one open case
 * per donor per organization per kind, so this is never a list that grows. A
 * request a person has answered is not offered back — the donor is told by the
 * answer, not by a row on a page.
 */
export const portalAccountSchema = z.object({
  donor: portalAccountDonorSchema,
  openRequests: z.array(portalPrivacyRequestSchema),
});

export type PortalAccount = z.infer<typeof portalAccountSchema>;

/**
 * `downloadable` is the server's answer to whether the PDF exists yet, so the
 * page offers a link that works rather than one that refuses. A receipt that is
 * voided is not listed at all.
 */
export const portalAccountReceiptSchema = z.object({
  id: z.uuid(),
  receiptNo: z.string(),
  kind: z.enum(['donation', 'annual_certificate']),
  issuedAt: z.string(),
  coversYear: z.number().int().nullable(),
  amount: moneySchema.nullable(),
  downloadable: z.boolean(),
});

export type PortalAccountReceipt = z.infer<typeof portalAccountReceiptSchema>;

export const portalAccountDonationSchema = z.object({
  referenceCode: z.string(),
  campaignName: z.string(),
  seasonYear: z.number().int(),
  /** Null for a request that took no money: there is no amount to show a donor. */
  amount: moneySchema.nullable(),
  quantity: z.number().int().nullable(),
  state: portalAccountDonationStateSchema,
  donatedAt: z.string(),
  receipt: portalAccountReceiptSchema.nullable(),
});

export type PortalAccountDonation = z.infer<typeof portalAccountDonationSchema>;

export const portalReceiptDownloadSchema = z.object({
  url: z.string(),
  fileName: z.string(),
  expiresInSeconds: z.number().int().positive(),
});

export type PortalReceiptDownload = z.infer<typeof portalReceiptDownloadSchema>;

/**
 * The profile a donor may move. **The address the account signs in with is not
 * on it**: moving it would need a link to the new mailbox before anything
 * changed, which is `auth.profile.changeEmail`'s whole machinery
 * (docs/decisions/0051) and is not built here — a donor whose address has
 * changed asks the organization, and the field is read-only on the page.
 *
 * No field carries a default, so an absent one is kept rather than filled in —
 * .claude/rules/api-contracts.md.
 */
/**
 * **Consent is not a field here.** A donor withdraws or grants on the channel
 * card beside this form, through `portalAccount.setNotificationChannel`, and
 * `donors.communication_consent` is the summary of those rows rather than a
 * switch of its own — docs/decisions/0061 §3, refused at the database by
 * migration `0075`.
 */
export const portalAccountProfileSchema = z.object({
  name: z.string().trim().min(2).max(200).optional(),
  phone: phoneSchema.nullable().optional(),
  address: z.string().trim().max(500).nullable().optional(),
  countryIso: z
    .string()
    .trim()
    .length(2)
    .regex(/^[A-Za-z]{2}$/)
    .nullable()
    .optional(),
});

export type PortalAccountProfileInput = z.infer<typeof portalAccountProfileSchema>;

/**
 * A channel the donor may turn on or off for themselves — the donor's half of
 * the same record an operator reads on the admin's donor page. It carries
 * **whether** they consented and **when**, and deliberately not who recorded it
 * or through what: those answer the organization's question about its own
 * lawfulness, and this page answers the donor's question about their own
 * choices. `addressPresent` is why a channel may be shown and not offered — a
 * donor with no telephone number has nowhere to receive an SMS, and the switch
 * says so rather than failing on save.
 */
export const portalNotificationChannelSchema = z.object({
  channel: z.enum(CONSENTABLE_NOTIFICATION_CHANNELS),
  enabled: z.boolean(),
  addressPresent: z.boolean(),
  decidedAt: z.string().nullable(),
});

export type PortalNotificationChannel = z.infer<typeof portalNotificationChannelSchema>;

export const portalNotificationChannelsSchema = z.object({
  items: z.array(portalNotificationChannelSchema),
});

export type PortalNotificationChannels = z.infer<typeof portalNotificationChannelsSchema>;

/**
 * One photograph or video of a donation's fulfilment, as a **60-second
 * presigned `GET`** — the file itself never passes through the API, and a link
 * that is copied out of the page stops working a minute later
 * (.claude/rules/file-uploads.md).
 */
export const portalEvidenceFileSchema = z.object({
  id: z.uuid(),
  kind: z.enum(['image', 'video']),
  contentType: z.string(),
  url: z.string(),
  expiresInSeconds: z.number().int().positive(),
});

export type PortalEvidenceFile = z.infer<typeof portalEvidenceFileSchema>;

/**
 * **What an evidence page may say, and all of it**: which campaign, the
 * donation's reference code, whom it was given on behalf of — a name, or none
 * — and the day it was carried out, beside the files.
 * Never the payer's name, telephone or address, never the reference person's,
 * never an amount: whoever holds the link may be none of those people —
 * docs/decisions/0075-evidence-travels-as-an-expiring-link.md.
 */
export const portalEvidenceSchema = z.object({
  referenceCode: z.string(),
  campaignName: z.string(),
  onBehalfOf: z.object({ name: z.string().nullable() }),
  fulfilledAt: z.string().nullable(),
  files: z.array(portalEvidenceFileSchema),
});

export type PortalEvidence = z.infer<typeof portalEvidenceSchema>;

const accountErrors = {
  ...portalErrors,
  UNAUTHENTICATED: commonErrors.UNAUTHENTICATED,
  CONFLICT: commonErrors.CONFLICT,
};

/**
 * `/account` — the donor area, docs/plans/06-portal-and-field.md §6.3.
 *
 * **A donor is not staff.** There is no `users` row, no password, no role and
 * no scope: the address proves itself by receiving a link, and the session that
 * follows lives in its own table under its own cookie. Everything behind it is
 * narrowed to two facts the request cannot name — the donor the link was minted
 * for, and the organization of the site it was minted on — so a donor reads
 * their own record, on one site, and nothing else exists as far as this surface
 * is concerned.
 */
export const portalAccountContract = {
  /**
   * Asks for a sign-in link. **The answer never says whether the address is
   * known**: identical bytes either way, one mail either way, and only the
   * trail records which it was — the same rule
   * `auth.requestPasswordReset` follows, and for a stronger reason here, since
   * confirming that an address has given to this organization is a disclosure
   * about a donor rather than about a staff account
   * (.claude/rules/auth-security.md, docs/decisions/0054 §3).
   */
  requestSignIn: oc
    .route({ method: 'POST', path: '/portal/account/sign-in-links' })
    .errors(accountErrors)
    .input(z.object({ email: z.email().max(320) }))
    .output(z.object({ accepted: z.literal(true) })),

  /**
   * Spends a link and starts a session. The token is spent **only on success**
   * and only under its own lock, so a link that loses a race, has been
   * superseded, has expired or was minted on another organization's site all
   * read the same refusal.
   */
  signIn: oc
    .route({ method: 'POST', path: '/portal/account/sessions' })
    .errors(accountErrors)
    .input(z.object({ token: z.string().min(1).max(200) }))
    .output(portalAccountSchema),

  signOut: oc
    .route({ method: 'POST', path: '/portal/account/sign-out' })
    .errors(accountErrors)
    .input(z.object({}))
    .output(z.object({ success: z.literal(true) })),

  me: oc
    .route({ method: 'GET', path: '/portal/account/me' })
    .errors(accountErrors)
    .input(z.object({}))
    .output(portalAccountSchema),

  donations: oc
    .route({ method: 'GET', path: '/portal/account/donations' })
    .errors(accountErrors)
    .input(
      paginationInputSchema.extend({
        pageSize: z.coerce.number().int().min(1).max(50).default(20),
      }),
    )
    .output(paginated(portalAccountDonationSchema)),

  receipts: oc
    .route({ method: 'GET', path: '/portal/account/receipts' })
    .errors(accountErrors)
    .input(
      paginationInputSchema.extend({
        pageSize: z.coerce.number().int().min(1).max(50).default(20),
      }),
    )
    .output(paginated(portalAccountReceiptSchema)),

  /**
   * §5.6's download, reached by a donor rather than by a seat. It is the same
   * 60-second presigned link over the same `receipts/<id>.pdf`, written into
   * the trail as `receipt` / `download` before the URL leaves the server — the
   * one thing that differs is which rows the reader may ask for.
   */
  downloadReceipt: oc
    .route({ method: 'GET', path: '/portal/account/receipts/{id}/download' })
    .errors(accountErrors)
    .input(z.object({ id: z.uuid() }))
    .output(portalReceiptDownloadSchema),

  /**
   * The fulfilment evidence of one of the donor's own donations on this site —
   * the same photographs and videos an evidence link opens, each a 60-second
   * presigned `GET`, the reading written into the trail before any link
   * leaves. A reference code that is not the donor's own, or not this site's,
   * is `NOT_FOUND`, exactly as a receipt that is not theirs is.
   */
  donationEvidence: oc
    .route({ method: 'GET', path: '/portal/account/donations/{referenceCode}/evidence' })
    .errors(accountErrors)
    .input(z.object({ referenceCode: z.string().trim().min(1).max(40) }))
    .output(portalEvidenceSchema),

  updateProfile: oc
    .route({ method: 'PATCH', path: '/portal/account/profile' })
    .errors(accountErrors)
    .input(portalAccountProfileSchema)
    .output(portalAccountSchema),

  /**
   * KVKK/GDPR: a donor asking for a copy of what the organization holds, or for
   * it to be erased. **The request is the ask, not the act** — a settled
   * donation, its receipt and the audit rows over them are what the
   * organization reports and are not the donor's to delete — so it is recorded,
   * alerted to the operations mailbox and answered by a person.
   */
  requestPrivacy: oc
    .route({ method: 'POST', path: '/portal/account/privacy-requests' })
    .errors(accountErrors)
    .input(
      z.object({
        kind: portalPrivacyRequestKindSchema,
        note: z.string().trim().max(1000).optional(),
      }),
    )
    .output(z.object({ request: portalPrivacyRequestSchema })),

  /**
   * The donor's own channels. It needs no permission beyond the donor session,
   * as every other route here does, and it names nobody but the caller.
   */
  notificationChannels: oc
    .route({ method: 'GET', path: '/portal/account/notification-channels' })
    .errors(accountErrors)
    .input(z.object({}))
    .output(portalNotificationChannelsSchema),

  /**
   * The donor changing their own mind. **Off is one press, exactly as on is** —
   * KVKK's standard is that withdrawal is as easy as consent, not that it is
   * possible — and the moment it is off nothing further leaves on that channel.
   */
  setNotificationChannel: oc
    .route({ method: 'PUT', path: '/portal/account/notification-channels/{channel}' })
    .errors(accountErrors)
    .input(
      z.object({
        channel: z.enum(CONSENTABLE_NOTIFICATION_CHANNELS),
        enabled: z.boolean(),
      }),
    )
    .output(portalNotificationChannelsSchema),
};

/**
 * A standing gift, as the two surfaces that read one both see it —
 * docs/plans/06-portal-and-field.md §6.4.
 *
 * **A sponsorship is not a second shape.** `beneficiaryRef` carries the answer
 * the campaign's own `beneficiaryRefFieldKey` names, so a sponsorship is a
 * pledge with a beneficiary and nothing in the type says otherwise.
 */
export const pledgeFrequencySchema = z.enum(['monthly', 'quarterly', 'yearly']);

export type PledgeFrequencyDto = z.infer<typeof pledgeFrequencySchema>;

export const pledgeStatusSchema = z.enum([
  'awaiting_mandate',
  'active',
  'paused',
  'payment_failed',
  'cancelled',
]);

export type PledgeStatusDto = z.infer<typeof pledgeStatusSchema>;

/**
 * What a donor is shown of the method they authorised. It is a brand, four
 * digits and a month — never a handle, never a token, and there is no route
 * that returns one: the provider holds the method and BTSis holds a reference
 * that moves nothing without the organization's own sealed key.
 */
export const pledgeMethodSchema = z.object({
  status: z.enum(['authorizing', 'active', 'revoked', 'failed']),
  brand: z.string().nullable(),
  last4: z.string().nullable(),
  expiry: z.string().nullable(),
});

export type PledgeMethod = z.infer<typeof pledgeMethodSchema>;

export const pledgeSchema = z.object({
  id: z.uuid(),
  status: pledgeStatusSchema,
  campaignId: z.uuid(),
  campaignName: z.string(),
  campaignSlug: z.string(),
  amount: moneySchema,
  quantity: z.number().int().nullable(),
  frequency: pledgeFrequencySchema,
  dayOfMonth: z.number().int(),
  startsOn: z.string(),
  nextDueOn: z.string().nullable(),
  lastChargedOn: z.string().nullable(),
  beneficiaryRef: z.string().nullable(),
  method: pledgeMethodSchema,
  createdAt: z.string(),
});

export type PledgeDto = z.infer<typeof pledgeSchema>;

/**
 * The donor's own view. It adds only what the donor has to act on: whether the
 * last period was taken, and whether anything is waiting on them.
 */
export const portalPledgeSchema = pledgeSchema.extend({
  lastChargeState: z.enum(['none', 'awaiting', 'succeeded', 'retrying', 'abandoned']),
  lastFailureKind: z.enum(['soft', 'hard', 'mandate', 'unavailable']).nullable(),
  nextAttemptAt: z.string().nullable(),
});

export type PortalPledge = z.infer<typeof portalPledgeSchema>;

const pledgeErrors = {
  ...portalErrors,
  CONFLICT: commonErrors.CONFLICT,
  PRICING_VIOLATION: commonErrors.PRICING_VIOLATION,
  ...formErrors,
};

/**
 * Setting a standing gift up, from the campaign page, before anybody signs in —
 * exactly as a one-off order is placed, on the same throttle key, and for the
 * same reason: a donor has no seat and no credential until money has arrived.
 *
 * **The first period is an ordinary hosted checkout.** The donor authorises a
 * real payment on the provider's own page and the provider keeps the method;
 * every later period is charged against what it kept. That is why there is no
 * separate "save my card" step and no card field anywhere — PCI scope stays at
 * the SAQ A floor that docs/decisions/0053 §3 put it at.
 */
export const portalPledgeContract = {
  create: oc
    .route({ method: 'POST', path: '/portal/pledges' })
    .errors(pledgeErrors)
    .input(
      z.object({
        line: portalBasketLineSchema,
        currency: currencySchema,
        frequency: pledgeFrequencySchema,
        dayOfMonth: z.coerce.number().int().min(1).max(28),
        startsOn: z
          .string()
          .regex(/^\d{4}-\d{2}-\d{2}$/)
          .optional(),
        donor: portalOrderDonorSchema,
      }),
    )
    .output(
      z.object({
        pledgeId: z.uuid(),
        orderId: z.uuid(),
        payment: portalPaymentHandoffSchema,
      }),
    ),
};

/** The donor's own management of what they have promised, behind the `/account` session. */
export const portalAccountPledgeContract = {
  list: oc
    .route({ method: 'GET', path: '/portal/account/pledges' })
    .errors(accountErrors)
    .output(z.object({ items: z.array(portalPledgeSchema) })),

  pause: oc
    .route({ method: 'POST', path: '/portal/account/pledges/{id}/pause' })
    .errors(accountErrors)
    .input(z.object({ id: z.uuid() }))
    .output(z.object({ pledge: portalPledgeSchema })),

  resume: oc
    .route({ method: 'POST', path: '/portal/account/pledges/{id}/resume' })
    .errors(accountErrors)
    .input(z.object({ id: z.uuid() }))
    .output(z.object({ pledge: portalPledgeSchema })),

  /**
   * The amount is **re-priced on the server** from the campaign's own period
   * price: the browser sends what was chosen and never what it comes to
   * (.claude/rules/money.md). It takes effect from the next period, because a
   * period already charged is money that has moved.
   */
  changeAmount: oc
    .route({ method: 'POST', path: '/portal/account/pledges/{id}/amount' })
    .errors({ ...accountErrors, PRICING_VIOLATION: commonErrors.PRICING_VIOLATION })
    .input(
      z.object({
        id: z.uuid(),
        quantity: z.coerce.number().int().min(1).max(1000).optional(),
        amountMinor: minorAmountSchema.optional(),
      }),
    )
    .output(z.object({ pledge: portalPledgeSchema })),

  cancel: oc
    .route({ method: 'POST', path: '/portal/account/pledges/{id}/cancel' })
    .errors(accountErrors)
    .input(z.object({ id: z.uuid(), reason: z.string().trim().max(500).optional() }))
    .output(z.object({ pledge: portalPledgeSchema })),
};

/**
 * The same three steps as the administrator's, for a donor with no seat. The
 * campaign must be one the site itself offers, and the route is throttled per
 * address like every other public write: a token commits one object into a
 * `pending` attachment nobody can read until the scan passes.
 */
export const portalFormFileContract = {
  requestUpload: oc
    .route({ method: 'POST', path: '/portal/campaigns/{campaignId}/form-files/uploads' })
    .errors(portalErrors)
    .input(
      formFileUploadInputSchema.extend({
        campaignId: z.uuid(),
        fileName: fileNameSchema,
        fileSize: z.number().int().min(1).max(FORM_FILE_MAX_BYTES),
      }),
    )
    .output(presignedUploadSchema),

  commit: oc
    .route({ method: 'POST', path: '/portal/campaigns/form-files' })
    .errors(portalErrors)
    .input(z.object({ uploadToken: uploadTokenSchema }))
    .output(z.object({ file: formFileSchema })),
};

/**
 * Any string at all: a token of the wrong shape is refused by the lookup with
 * the same answer an unknown one gets, never by a validation error that would
 * tell a caller what a real one looks like.
 */
const evidenceTokenSchema = z.string().min(1).max(512);

/**
 * The evidence link's page — §3.1 step 3 and §4.4. **The token is the whole
 * capability**, the answer is narrowed to the organization the host resolved,
 * and an unknown, expired, revoked or wrong-site token is one answer: 404
 * `EVIDENCE_LINK_UNAVAILABLE`, the same bytes every time. `POST` because an
 * opening is recorded — a count on the link and an audit row before any file
 * link leaves — and because a token in a body is not a token in an access log.
 */
export const portalEvidenceContract = {
  open: oc
    .route({ method: 'POST', path: '/portal/evidence/open' })
    .errors(portalErrors)
    .input(z.object({ token: evidenceTokenSchema }))
    .output(portalEvidenceSchema),

  /**
   * Whoever holds the link saying they want no more — most often the
   * reference person, who never asked this organization for anything. It is
   * the staff route's own write: the preference is dated and kept, and the
   * link stops opening in the same transaction.
   */
  optOut: oc
    .route({ method: 'POST', path: '/portal/evidence/opt-out' })
    .errors(portalErrors)
    .input(z.object({ token: evidenceTokenSchema }))
    .output(z.object({ optedOut: z.literal(true) })),
};

export const donationPaymentPageSchema = z.object({
  referenceCode: z.string(),
  campaignName: z.string(),
  payingOrganizationName: z.string().nullable(),
  remaining: moneySchema,
  status: z.enum(['payable', 'paid', 'unavailable']),
  accounts: z.array(
    z.object({
      id: z.uuid(),
      label: z.string(),
      provider: z.enum(['stripe', 'paypal', 'nestpay']),
      organizationId: z.uuid(),
      organizationName: z.string(),
    }),
  ),
});
export const portalDonationPaymentContract = {
  get: oc
    .route({ method: 'GET', path: '/portal/donation-payments/{referenceCode}' })
    .errors(portalErrors)
    .input(z.object({ referenceCode: z.string().min(1).max(32) }))
    .output(donationPaymentPageSchema),
  checkout: oc
    .route({ method: 'POST', path: '/portal/donation-payments/{referenceCode}/checkout' })
    .errors({ ...portalErrors, CONFLICT: commonErrors.CONFLICT })
    .input(z.object({ referenceCode: z.string().min(1).max(32), accountId: z.uuid() }))
    .output(z.object({ url: z.string() })),
};

export const portalContract = {
  donationPayment: portalDonationPaymentContract,
  campaign: portalCampaignContract,
  formFile: portalFormFileContract,
  order: portalOrderContract,
  evidence: portalEvidenceContract,
  account: { ...portalAccountContract, pledge: portalAccountPledgeContract },
  pledge: portalPledgeContract,
};

/**
 * What the donation site's oRPC client is built from, and the whole reason
 * `@btsis/contracts/portal` exists. The package's one entry point pulls the
 * administrator's contracts — users, roles, audit, donations — into a bundle
 * served to the open internet, for schemas the donor's browser never uses.
 * This module reaches only into `common/`, so importing it carries the public
 * surface and nothing else.
 *
 * `campaign.quote` is the same procedure `campaignContract.quote` declares, so
 * there is still one `GET /campaigns/quote` and one handler behind it; the
 * namespaces here are the client's call sites, not a second route.
 */
export const portalClientContract = {
  organization: { branding: organizationBrandingContract },
  portal: portalContract,
  campaign: { quote: campaignQuoteContract },
};

export type PortalClientContract = typeof portalClientContract;
